import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const PrivacyPolicy = () => {
  const tableOfContents = [
    { id: 'information-we-collect', title: 'Information We Collect' },
    { id: 'how-we-use-information', title: 'How We Use Your Information' },
    { id: 'information-sharing', title: 'Information Sharing' },
    { id: 'your-rights', title: 'Your Rights and Choices' },
    { id: 'security', title: 'Security and Retention' },
    { id: 'children', title: 'Children\'s Privacy' },
    { id: 'changes', title: 'Changes to This Policy' },
    { id: 'contact', title: 'Contact Us' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <section className="py-20">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Table of Contents - Sticky on desktop */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                <h2 className="text-lg font-semibold text-foreground mb-4">Table of Contents</h2>
                <nav className="space-y-2">
                  {tableOfContents.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {item.title}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <h1 className="text-4xl font-bold text-foreground mb-4">Privacy Policy</h1>
              <p className="text-muted-foreground mb-8">Last updated: January 2024</p>
              
              <div className="prose prose-lg max-w-none space-y-8">
                <div className="text-muted-foreground leading-relaxed">
                  <p className="mb-6">
                    Safurra helps consumers compare life insurance options and connect with licensed carriers and partners. 
                    This Privacy Policy explains what information we collect, why we collect it, how we use it, and your choices regarding your information.
                  </p>
                </div>

                <section id="information-we-collect">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Information We Collect</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p><strong className="text-foreground">Information You Provide:</strong> Account details, quote requests, medical pre-screening answers, contact information, and communications with our support team.</p>
                    <p><strong className="text-foreground">Automatically Collected:</strong> Device information, IP address, browser type, pages visited, referral sources, and usage analytics through cookies and similar technologies.</p>
                    <p><strong className="text-foreground">Third-Party Sources:</strong> Information from analytics providers, advertising partners, and public databases to enhance our services and prevent fraud.</p>
                  </div>
                </section>

                <section id="how-we-use-information">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">How We Use Your Information</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>We use your information to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Generate accurate insurance quotes and eligibility assessments</li>
                      <li>Screen for eligibility and prevent fraud</li>
                      <li>Comply with legal and regulatory requirements</li>
                      <li>Improve our products and services</li>
                      <li>Provide customer support and respond to inquiries</li>
                      <li>Send marketing communications (with your consent)</li>
                      <li>Analyze usage patterns and optimize user experience</li>
                    </ul>
                  </div>
                </section>

                <section id="information-sharing">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Information Sharing</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p><strong className="text-foreground">Carriers and Partners:</strong> We share necessary information with insurance carriers and partners to obtain quotes and complete applications on your behalf.</p>
                    <p><strong className="text-foreground">Service Providers:</strong> We work with hosting providers, analytics services, and email platforms that help us operate our business.</p>
                    <p><strong className="text-foreground">Legal Requirements:</strong> We may disclose information when required by law, court order, or to protect our rights and safety.</p>
                    <p><strong className="text-foreground">No Sale of Personal Information:</strong> We do not sell your personal information to third parties. For targeted advertising choices, <a href="mailto:support@safurra.com" className="text-primary hover:underline">contact us</a>.</p>
                  </div>
                </section>

                <section id="your-rights">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Your Rights and Choices</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>You have the right to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong className="text-foreground">Access:</strong> Request a copy of your personal information</li>
                      <li><strong className="text-foreground">Correction:</strong> Update or correct inaccurate information</li>
                      <li><strong className="text-foreground">Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                      <li><strong className="text-foreground">Opt-Out:</strong> Unsubscribe from marketing emails and SMS messages</li>
                      <li><strong className="text-foreground">Cookie Preferences:</strong> Manage cookie settings through your browser</li>
                    </ul>
                    <p>Residents of California, Virginia, Colorado, and Connecticut may have additional privacy rights under state law.</p>
                  </div>
                </section>

                <section id="security">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Security and Retention</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>We implement appropriate technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or destruction.</p>
                    <p>We retain your information only as long as necessary to fulfill the purposes outlined in this policy or as required by legal and regulatory obligations.</p>
                  </div>
                </section>

                <section id="children">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Children's Privacy</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.</p>
                  </div>
                </section>

                <section id="changes">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Changes to This Policy</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "Last updated" date.</p>
                  </div>
                </section>

                <section id="contact">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Contact Us</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>If you have questions about this Privacy Policy or want to exercise your privacy rights, please contact us:</p>
                    <p>Email: <a href="mailto:support@safurra.com" className="text-primary hover:underline">support@safurra.com</a></p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;